/**
 * All-in-one setup: authenticate → detect agents → install skill + MCP config
 */

import { isAuthenticated, readCredentials, isExpired, recordConfiguredAgents } from "../auth/credentials.js";
import { oauthBrowserFlow, exchangeToken } from "../auth/oauth.js";
import { detectAgents } from "../agents/detector.js";
import { agents, getAgent } from "../agents/index.js";
import { runDoctor } from "./doctor.js";

interface SetupOpts {
  token?: string;
  graphqlUrl?: string;
  global?: boolean;
  agents?: string;
  yes?: boolean;
}

export async function runSetup(opts: SetupOpts): Promise<void> {
  console.log("\n🐟 Sailfish Autofix Setup\n");

  // Step 1: Authentication
  console.log("Step 1/3: Authentication");
  if (opts.token) {
    console.log("  Using provided token...");
    await exchangeToken(opts.token, opts.graphqlUrl);
    console.log("  ✓ Authenticated via token\n");
  } else if (isAuthenticated()) {
    const creds = readCredentials();
    console.log(`  ✓ Already authenticated (${creds?.graphqlUrl || "default"})\n`);
  } else {
    const creds = readCredentials();
    if (creds && isExpired(creds)) {
      console.log("  ⚠️  Credentials expired — re-authenticating...");
    } else {
      console.log("  Opening browser for login...");
    }
    await oauthBrowserFlow();
    console.log("  ✓ Authenticated\n");
  }

  // Step 2: Detect agents
  console.log("Step 2/3: Detecting AI coding agents");
  const detected = detectAgents();
  const filterIds = opts.agents?.split(",").map((s) => s.trim().toLowerCase());

  const toInstall = detected.filter((d) => {
    if (!d.detected) return false;
    if (filterIds && !filterIds.includes(d.id)) return false;
    return true;
  });

  if (toInstall.length === 0) {
    console.log("  No supported agents detected on this system.");
    console.log("  Supported agents: " + Object.keys(agents).join(", "));
    console.log("  Use --agents to specify manually.\n");

    // Still install the cross-client skill
    console.log("  Installing cross-client skill (.agents/skills/)...");
    const anyAgent = getAgent("claude-code")!;
    anyAgent.installSkill(opts.global ?? true);
    console.log("  ✓ Cross-client skill installed\n");
    return;
  }

  console.log(`  Found ${toInstall.length} agent(s):`);
  for (const agent of toInstall) {
    const skillStatus = agent.hasSkill ? "skill ✓" : "skill ✗";
    const mcpStatus = agent.hasMcp ? "mcp ✓" : "mcp ✗";
    console.log(`    - ${agent.name} (${skillStatus}, ${mcpStatus})`);
  }
  console.log();

  // Confirmation
  if (!opts.yes) {
    const { createInterface } = await import("readline");
    const rl = createInterface({ input: process.stdin, output: process.stdout });
    const answer = await new Promise<string>((resolve) => {
      rl.question("  Proceed with installation? [Y/n] ", resolve);
    });
    rl.close();
    if (answer.toLowerCase() === "n") {
      console.log("  Cancelled.\n");
      return;
    }
  }

  // Step 3: Install
  console.log("Step 3/3: Installing skill and MCP configuration");
  const isGlobal = opts.global ?? true;

  for (const agent of toInstall) {
    const writer = getAgent(agent.id);
    if (!writer) continue;

    try {
      writer.installSkill(isGlobal);
      console.log(`  ✓ ${agent.name} — skill installed`);
    } catch (err: any) {
      console.error(`  ✗ ${agent.name} — skill failed: ${err.message}`);
    }

    try {
      writer.installMcp();
      console.log(`  ✓ ${agent.name} — MCP configured`);
    } catch (err: any) {
      console.error(`  ✗ ${agent.name} — MCP failed: ${err.message}`);
    }
  }

  // Remember the selection (not the install outcome) so doctor checks these
  // agents and only these — a half-failed install should still be diagnosed.
  recordConfiguredAgents(toInstall.map((a) => a.id));

  console.log("\n✅ Setup complete!");
  console.log("   The Sailfish Autofix skill and MCP server are now configured.");
  console.log("   Open any supported agent and use /sailfish-autofix to investigate issues.\n");

  // Run doctor to verify everything works
  console.log("Running diagnostics...\n");
  await runDoctor();
}
